import React, { useState, useEffect, useMemo } from 'react';
import './OffsetRuleEditor.css';

export type OffsetType = 
  | 'after_project_start'
  | 'before_ifc'
  | 'before_construction'
  | 'after_previous_step'
  | `anchor:${string}`;

export interface OffsetRule {
  offsetType: OffsetType;
  offsetDays: number;
  referenceStepId?: string;
}

interface OffsetRuleEditorProps {
  value: OffsetRule;
  onChange: (rule: OffsetRule) => void;
  availableSteps?: Array<{ stepId: string; name: string; stepOrder: number }>;
  currentStepId?: string;
  customAnchors?: Array<{ key: string; label: string }>;
}

const DAYS_PER_MONTH = 30;

function decompose(totalDays: number): { months: number; days: number } {
  const abs = Math.abs(totalDays);
  return { months: Math.floor(abs / DAYS_PER_MONTH), days: abs % DAYS_PER_MONTH };
}

function compose(months: number, days: number, direction: 'after' | 'before'): number {
  const total = months * DAYS_PER_MONTH + days;
  return direction === 'before' ? -total : total;
}

const OffsetRuleEditor: React.FC<OffsetRuleEditorProps> = ({
  value,
  onChange,
  availableSteps = [],
  currentStepId,
  customAnchors = [],
}) => {
  const [localRule, setLocalRule] = useState<OffsetRule>(value);
  const [validationWarning, setValidationWarning] = useState<string | null>(null);

  const { months, days } = decompose(localRule.offsetDays);
  const direction: 'after' | 'before' = localRule.offsetDays >= 0 ? 'after' : 'before';

  useEffect(() => {
    setLocalRule(value);
  }, [value]);

  const referenceableSteps = useMemo(() => {
    return availableSteps
      .filter(step => step.stepId !== currentStepId)
      .sort((a, b) => a.stepOrder - b.stepOrder);
  }, [availableSteps, currentStepId]);

  const encodedValue = useMemo(() => {
    if (localRule.offsetType === 'after_previous_step' && localRule.referenceStepId) {
      return `step:${localRule.referenceStepId}`;
    }
    return localRule.offsetType;
  }, [localRule.offsetType, localRule.referenceStepId]);

  const updateRule = (updates: Partial<OffsetRule>) => {
    const newRule: OffsetRule = { ...localRule, ...updates };

    let warning: string | null = null;
    if (newRule.offsetType === 'after_previous_step' && !newRule.referenceStepId && referenceableSteps.length === 0) {
      warning = 'No other steps available to reference yet';
    }

    setValidationWarning(warning);
    setLocalRule(newRule);
    onChange(newRule);
  };

  const handleMonthsChange = (newMonths: number) => {
    const clamped = Math.max(0, Math.min(24, newMonths));
    updateRule({ offsetDays: compose(clamped, days, direction) });
  };

  const handleDaysChange = (newDays: number) => {
    const clamped = Math.max(0, Math.min(29, newDays));
    updateRule({ offsetDays: compose(months, clamped, direction) });
  };

  const handleDirectionChange = (newDir: 'after' | 'before') => {
    updateRule({ offsetDays: compose(months, days, newDir) });
  };

  const handleReferenceChange = (encoded: string) => {
    if (encoded.startsWith('step:')) {
      const stepId = encoded.replace('step:', '');
      updateRule({ offsetType: 'after_previous_step', referenceStepId: stepId });
    } else {
      updateRule({
        offsetType: encoded as OffsetType,
        referenceStepId: undefined,
      });
    }
  };

  const getReferenceName = (): string => {
    const type = localRule.offsetType;

    if (type === 'after_previous_step') {
      const ref = availableSteps.find(s => s.stepId === localRule.referenceStepId);
      return ref ? ref.name : 'the previous step';
    }
    if (type.startsWith('anchor:')) {
      const key = type.replace('anchor:', '');
      const anchor = customAnchors.find(c => c.key === key);
      return anchor?.label || key;
    }

    switch (type) {
      case 'after_project_start': return 'Project Start Date';
      case 'before_ifc': return 'Issued for Construction (IFC) Date';
      case 'before_construction': return 'Construction Start Date';
      default: return 'the reference date';
    }
  };

  const buildPreview = (): string => {
    const totalDays = Math.abs(localRule.offsetDays);
    if (totalDays === 0) return `Same day as ${getReferenceName()}`;

    const parts: string[] = [];
    if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);

    return `${parts.join(' and ')} ${direction} ${getReferenceName()}`;
  };

  return (
    <div className="offset-rule-editor">
      <div className="offset-rule-sentence">
        <span className="offset-sentence-label">Schedule this step</span>
        <div className="offset-sentence-controls">
          <div className="offset-duration-group">
            <div className="offset-unit">
              <div className="offset-stepper">
                <button type="button" className="stepper-btn" onClick={() => handleMonthsChange(months - 1)} aria-label="Decrease months">−</button>
                <input
                  type="number"
                  className="stepper-field"
                  value={months}
                  onChange={(e) => handleMonthsChange(parseInt(e.target.value, 10) || 0)}
                  min="0"
                  max="24"
                />
                <button type="button" className="stepper-btn" onClick={() => handleMonthsChange(months + 1)} aria-label="Increase months">+</button>
              </div>
              <span className="offset-unit-label">{months === 1 ? 'month' : 'months'}</span>
            </div>
            <span className="offset-and">and</span>
            <div className="offset-unit">
              <div className="offset-stepper">
                <button type="button" className="stepper-btn" onClick={() => handleDaysChange(days - 1)} aria-label="Decrease days">−</button>
                <input
                  type="number"
                  className="stepper-field"
                  value={days}
                  onChange={(e) => handleDaysChange(parseInt(e.target.value, 10) || 0)}
                  min="0"
                  max="29"
                />
                <button type="button" className="stepper-btn" onClick={() => handleDaysChange(days + 1)} aria-label="Increase days">+</button>
              </div>
              <span className="offset-unit-label">{days === 1 ? 'day' : 'days'}</span>
            </div>
          </div>

          <select
            className="offset-dir-select"
            value={direction}
            onChange={(e) => handleDirectionChange(e.target.value as 'after' | 'before')}
          >
            <option value="after">after</option>
            <option value="before">before</option>
          </select>

          <select
            className="offset-ref-select"
            value={encodedValue}
            onChange={(e) => handleReferenceChange(e.target.value)}
          >
            <optgroup label="Key Project Dates">
              <option value="after_project_start">Project Start Date</option>
              {customAnchors.map(anchor => (
                <option key={anchor.key} value={`anchor:${anchor.key}`}>
                  {anchor.label}
                </option>
              ))}
            </optgroup>
            {referenceableSteps.length > 0 && (
              <optgroup label="After Another Step">
                {referenceableSteps.map(step => (
                  <option key={step.stepId} value={`step:${step.stepId}`}>
                    Step {step.stepOrder}: {step.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      <div className="offset-preview">
        <span className="offset-preview-icon">&#128197;</span>
        <span className="offset-preview-text">{buildPreview()}</span>
      </div>

      {validationWarning && (
        <div className="validation-warning">
          ⚠️ {validationWarning}
        </div>
      )}
    </div>
  );
};

export default OffsetRuleEditor;
